import Editor from '@monaco-editor/react';
import { Alert, Box, Button, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { checkGuideHtml } from '@shared/guideHtmlRules';

import GuideImagePicker, { type PickedGuideImage } from './GuideImagePicker';
import { guidePreviewDocument } from './guidePreviewDocument';
import RevisionChat from './RevisionChat';

interface HtmlBodyEditorProps {
  value: string;
  onChange: (next: string) => void;
  /** The guide's title, so a body that repeats it as its first heading can be flagged. */
  title?: string;
  /** Shown above the editor when the generator could not fix everything on its retry. */
  generatorProblems?: string[];
  /** Slug of the guide being edited, used to name uploaded images readably. */
  slug?: string;
}

/** How long to wait after a keystroke before re-rendering the preview. */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The guide body, edited as HTML beside a live preview.
 *
 * Monaco rather than a textarea because this is a document, not a field: folding, bracket matching
 * and tag completion are the difference between editing HTML and fighting it. The same editor the
 * email templates already use, in the same layout, so an operator who has edited one knows this one.
 *
 * **The preview renders in an iframe with its own document.** A guide body is styled by inline
 * `var(--token)` references, and those resolve against whatever `:root` the document has: rendering
 * the preview inside the Control Center would resolve them against the console's dark theme and
 * show colours the public page will never use. The iframe carries the public site's values instead,
 * so the preview is wrong in a known, small way (fonts, surrounding chrome) rather than in a
 * confident and invisible one.
 *
 * The preview is deliberately not sanitised, and that is the one thing to know about it: it shows
 * what the operator wrote, while the public page shows what survives sanitising. A `<script>` typed
 * here would run inside the iframe. It is sandboxed for that reason.
 */
const HtmlBodyEditor: React.FC<HtmlBodyEditorProps> = ({
  value,
  onChange,
  title,
  generatorProblems,
  slug = 'guide',
}) => {
  const [preview, setPreview] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monaco's editor instance, kept so an image can be inserted where the caret is rather than
  // appended to the end. Appending would mean every picture lands after the conclusion and has to
  // be cut and pasted into place, which is the kind of friction that stops people adding any.
  const editorRef = useRef<Parameters<NonNullable<React.ComponentProps<typeof Editor>['onMount']>>[0] | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setPreview(value), PREVIEW_DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value]);

  // Recomputed on every keystroke rather than on save: the rules are what block publishing, so an
  // operator should see a violation while they are still looking at the line that caused it.
  const problems = useMemo(() => checkGuideHtml(value, title), [value, title]);

  const srcDoc = useMemo(() => guidePreviewDocument(preview), [preview]);

  /**
   * Put a figure where the caret is.
   *
   * `<figure>` rather than a bare `<img>`: the public sanitiser allows figure, figcaption and img
   * together, `GuideHtmlBody.module.scss` already styles that trio, and a caption has nowhere to
   * live without it. Written on its own lines so the surrounding HTML stays readable in the
   * editor, since this is a document an operator will keep editing by hand.
   */
  const insertImage = (image: PickedGuideImage) => {
    const escape = (text: string) =>
      text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const figure =
      `\n<figure>\n  <img src="${escape(image.url)}" alt="${escape(image.alt)}" loading="lazy" />\n` +
      (image.caption ? `  <figcaption>${escape(image.caption)}</figcaption>\n` : '') +
      `</figure>\n`;

    const editor = editorRef.current;
    if (!editor) {
      // No editor instance yet: appending still beats losing the upload the author just made.
      onChange(`${value}${figure}`);
      return;
    }
    const selection = editor.getSelection();
    if (!selection) {
      onChange(`${value}${figure}`);
      return;
    }
    editor.executeEdits('insert-guide-image', [
      { range: selection, text: figure, forceMoveMarkers: true },
    ]);
    editor.focus();
  };

  return (
    <Box className="ge-html">
      {generatorProblems && generatorProblems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The generator could not fix {generatorProblems.length} house-rule problem(s) on its own
          retry. They are listed below and have to be fixed before this can be published.
        </Alert>
      )}

      {problems.length > 0 ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {problems.length} house-rule problem(s)
          </Typography>
          {problems.slice(0, 6).map((p) => (
            <Typography key={p} variant="body2">
              {p}
            </Typography>
          ))}
          {problems.length > 6 && (
            <Typography variant="body2">and {problems.length - 6} more.</Typography>
          )}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          The body follows the house rules.
        </Alert>
      )}

      <Box className="ge-html__tools">
        <GuideImagePicker
          slug={slug}
          label="Insert image"
          withCaption
          onPicked={insertImage}
        />
        <Typography variant="caption">
          Inserted where the cursor is, as a figure. Alt text is asked for first because the house
          rules refuse to publish an image without one.
        </Typography>
      </Box>

      <Box className="ge-html__split">
        <Box className="ge-html__code">
          <Editor
            height="100%"
            language="html"
            theme="vs-dark"
            value={value}
            onChange={(next) => onChange(next ?? '')}
            onMount={(editor) => {
              editorRef.current = editor;
            }}
            options={{
              minimap: { enabled: false },
              wordWrap: 'on',
              // No fontSize. Monaco wants a number, so a token cannot be passed, and a raw number
              // is what the size guardrail exists to stop. Its default is close enough that the
              // difference is not worth either. (TemplateEditor passes a var() string here, which
              // Monaco cannot read, so that editor is on the default too.)
              tabSize: 2,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </Box>
        <Box className="ge-html__preview">
          <iframe
            title="Guide preview"
            className="ge-html__frame"
            // No allow-scripts: a body that tries to run something must not run it here either.
            sandbox=""
            srcDoc={srcDoc}
          />
        </Box>
      </Box>

      {/* The chat sits under the split rather than beside it: the editor and the preview both need
          width to be usable, and a third column would take it from whichever one you were reading. */}
      <RevisionChat html={value} onRevised={onChange} />

      <Box className="ge-html__foot">
        <Typography variant="caption">
          The preview shows what you wrote. The published page shows what survives sanitising, and
          drops anything the rules above flag.
        </Typography>
        <Button
          size="small"
          onClick={() => setPreview(value)}
          disabled={preview === value}
        >
          Refresh preview
        </Button>
      </Box>
    </Box>
  );
};

export default HtmlBodyEditor;
